import { Link } from '@tanstack/react-router';
import {
  Camera,
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  Image as ImageIcon,
  WifiOff,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@up2d8/ui';
import type { MediaPurpose } from '@up2d8/contracts';
import { useUpload } from '../../../shared/use-upload';
import './floor.css';

/**
 * De onderdelen van de vloermodus (VLR-1), gedeeld door Checklists, Formulieren en Melden.
 *
 * Elke klassenaam hier komt uit deel B van de drie replica-specs en staat in `floor.css`. Wat in de
 * mockup een plaatje van een veld is, is hier een echt veld; verder is de vorm dezelfde.
 */

export interface Crumb {
  label: string;
  /** Zonder `to` is het de plek waar je nu bent. */
  to?: string;
  onClick?: () => void;
}

/** Het kruimelpad boven de titel, alleen op het bureau (spec §1, "Indeling per breedte"). */
export function Crumbs({ items }: { items: Crumb[] }) {
  const { t } = useTranslation();
  return (
    <nav className="cf-crumb" aria-label={t('floor.breadcrumb')} data-testid="floor-crumb">
      {items.map((item, index) => (
        <span key={item.label} className="flex items-center gap-1.5">
          {index > 0 ? <ChevronRight aria-hidden width={14} height={14} /> : null}
          {item.to ? (
            <Link to={item.to}>{item.label}</Link>
          ) : item.onClick ? (
            <button type="button" className="text-[var(--fm-primary)]" onClick={item.onClick}>
              {item.label}
            </button>
          ) : (
            <span aria-current="page">{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

export type BadgeTone = 'neutral' | 'attention' | 'ok' | 'danger' | 'info';

/** Altijd een woord en waar nuttig een icoon van 14: nooit alleen kleur. */
export function FloorBadge({
  tone = 'neutral',
  icon,
  children,
  testId,
}: {
  tone?: BadgeTone;
  icon?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <span className={cn('fm-badge', tone !== 'neutral' && tone)} data-testid={testId}>
      {icon}
      {children}
    </span>
  );
}

/** De offline-strook: telefoon direct onder de kopbalk, bureau bovenaan de inhoud. */
export function OfflineStrip({ children }: { children: ReactNode }) {
  return (
    <div className="cf-offline" role="status" data-testid="floor-offline">
      <WifiOff aria-hidden width={20} height={20} className="shrink-0" />
      <span>{children}</span>
    </div>
  );
}

export interface FloorToastMessage {
  tone: 'ok' | 'err';
  text: string;
}

/**
 * De melding onderaan (spec §1): zes seconden, donker, met een groen of rood icoon.
 *
 * Een eigen melding en niet de toast van het portaal: die staat rechtsboven, en op een telefoon aan
 * de lijn kijk je naar de knop die je net indrukte, onderaan.
 */
export function useFloorToast() {
  const [message, setMessage] = useState<FloorToastMessage | null>(null);
  const timer = useRef<number | null>(null);

  const show = useCallback((next: FloorToastMessage) => {
    if (timer.current) window.clearTimeout(timer.current);
    setMessage(next);
    timer.current = window.setTimeout(() => setMessage(null), 6000);
  }, []);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  const element = message ? (
    <div
      className={cn('cf-toast', message.tone)}
      role={message.tone === 'err' ? 'alert' : 'status'}
      data-testid="floor-toast"
    >
      {message.tone === 'ok' ? (
        <CheckCircle2 aria-hidden width={22} height={22} />
      ) : (
        <CircleAlert aria-hidden width={22} height={22} />
      )}
      <span>{message.text}</span>
    </div>
  ) : null;

  return { show, element };
}

/** Placeholderkaarten in de vorm van de lijstkaart. */
export function SkeletonCards({ count = 3 }: { count?: number }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-[inherit]" aria-busy="true" aria-label={t('common.loading')}>
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="fm-card sk-card" aria-hidden>
          <div className="sk sk-circle" />
          <div className="sk-stack">
            <div className="sk sk-title" style={{ width: '70%' }} />
            <div className="sk sk-line" style={{ width: '45%' }} />
            <div className="sk sk-line" style={{ width: '90%', height: 8 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Lege toestand: rond vlak van 72, titel van 18 en één zin. */
export function EmptyCard({
  icon,
  tone = 'ok',
  title,
  text,
  testId,
}: {
  icon: ReactNode;
  tone?: 'ok' | 'attention' | 'danger' | 'neutral';
  title: string;
  text: string;
  testId?: string;
}) {
  return (
    <div className="fm-card cf-empty" data-testid={testId}>
      <div className={cn('fm-hero-icon', tone !== 'neutral' && tone)}>{icon}</div>
      <div className="fm-title" style={{ fontSize: 18 }}>
        {title}
      </div>
      <div className="fm-meta" style={{ justifyContent: 'center' }}>
        {text}
      </div>
    </div>
  );
}

/** Een veldfout: rood, Poppins 600, 14 px, met icoon. */
export function FieldError({ children, id }: { children: ReactNode; id?: string }) {
  return (
    <div className="fm-error" role="alert" id={id}>
      <CircleAlert aria-hidden width={16} height={16} />
      <span>{children}</span>
    </div>
  );
}

export interface Photo {
  fileId: string;
  /** Een voorbeeld van het toestel zelf; een foto die al op de server stond, heeft er geen. */
  previewUrl: string | null;
}

/**
 * Foto's: miniaturen van 80, een verwijderknop met een raakvlak van 48, en de tegel "Foto
 * toevoegen" als laatste in de rij (spec §1).
 *
 * Een foto vraagt verbinding: een bestand-id dat nog niet bestaat, kan niet in de wachtrij. Offline
 * is het vak daarom uitgeschakeld, met een zin die zegt waarom.
 */
export function PhotoPicker({
  photos,
  max,
  onChange,
  ownerModule,
  purpose,
  disabled = false,
  offline = false,
  offlineHint,
  testId,
}: {
  photos: Photo[];
  max: number;
  onChange: (photos: Photo[]) => void;
  ownerModule: string;
  purpose: MediaPurpose;
  disabled?: boolean;
  offline?: boolean;
  offlineHint: string;
  testId: string;
}) {
  const { t } = useTranslation();
  const upload = useUpload();
  const uit = disabled || offline;

  const kies = async (file: File | undefined) => {
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    const fileId = await upload.upload(file, { ownerModule, purpose });
    if (!fileId) {
      URL.revokeObjectURL(previewUrl);
      return;
    }
    onChange([...photos, { fileId, previewUrl }]);
  };

  const input = (
    <input
      type="file"
      accept="image/*"
      className="sr-only"
      disabled={uit}
      data-testid={`${testId}-input`}
      onChange={(event) => {
        void kies(event.target.files?.[0]);
        event.target.value = '';
      }}
    />
  );

  const bezig = upload.progress !== null;

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      {photos.length === 0 && !bezig ? (
        <label className={cn('cf-photobox', uit && 'disabled')} aria-disabled={uit || undefined}>
          <Camera aria-hidden width={24} height={24} />
          {t('floor.addPhoto')}
          {input}
        </label>
      ) : (
        <div className="cf-thumbs">
          {photos.map((photo, index) => (
            <div key={photo.fileId} className="cf-thumb" data-testid={`${testId}-thumb`}>
              {photo.previewUrl ? (
                <img src={photo.previewUrl} alt="" />
              ) : (
                <ImageIcon aria-hidden width={28} height={28} />
              )}
              {!disabled ? (
                <button
                  type="button"
                  className="cf-thumb-x"
                  aria-label={t('floor.removePhoto', { n: index + 1 })}
                  onClick={() => onChange(photos.filter((other) => other.fileId !== photo.fileId))}
                >
                  <span>
                    <X aria-hidden width={14} height={14} strokeWidth={2.5} />
                  </span>
                </button>
              ) : null}
            </div>
          ))}
          {bezig ? (
            <div
              className="cf-thumb uploading"
              role="progressbar"
              aria-valuenow={upload.progress ?? 0}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              {upload.progress}%
              <div className="fm-progress">
                <span style={{ width: `${upload.progress ?? 0}%` }} />
              </div>
            </div>
          ) : null}
          {photos.length + (bezig ? 1 : 0) < max ? (
            <label
              className={cn('cf-thumb add', uit && 'disabled')}
              aria-disabled={uit || undefined}
            >
              <Camera aria-hidden width={22} height={22} />
              {t('floor.addPhoto')}
              {input}
            </label>
          ) : null}
        </div>
      )}
      {bezig ? (
        <div className="fm-hint">
          {t('floor.photoProgress', { count: photos.length, progress: upload.progress })}
        </div>
      ) : null}
      {upload.error ? <FieldError>{upload.error}</FieldError> : null}
      {offline ? <div className="fm-hint">{offlineHint}</div> : null}
    </div>
  );
}

/**
 * "vandaag 08:00", "morgen 07:30", "vr 18/09 · 14:00" (spec §2): een deadline lees je als een
 * dag en een uur, niet als een datum.
 */
export function useWhen() {
  const { t, i18n } = useTranslation();
  return useCallback(
    (iso: string, now: Date = new Date()): string => {
      const date = new Date(iso);
      const time = new Intl.DateTimeFormat(i18n.language, {
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
      const dag = (offset: number) => {
        const day = new Date(now);
        day.setDate(day.getDate() + offset);
        return day.toDateString() === date.toDateString();
      };
      if (dag(0)) return t('floor.today', { time });
      if (dag(1)) return t('floor.tomorrow', { time });
      if (dag(-1)) return t('floor.yesterday', { time });
      const weekday = new Intl.DateTimeFormat(i18n.language, { weekday: 'short' })
        .format(date)
        .replace('.', '');
      const dayMonth = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
      return t('floor.dayTime', { day: `${weekday} ${dayMonth}`, time });
    },
    [t, i18n.language],
  );
}

/** Een uur: "06:12". */
export function useClock() {
  const { i18n } = useTranslation();
  return useCallback(
    (iso: string) =>
      new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' }).format(
        new Date(iso),
      ),
    [i18n.language],
  );
}

/**
 * Een melding die een navigatie overleeft.
 *
 * "De checklist is afgerond." hoort op de lijst te staan, en de lijst is een ander adres dan het
 * invulscherm. Eén waarde in het geheugen van de pagina, opgehaald door het volgende scherm dat
 * opent; geen opslag, want na een herlaadbeurt hoort de melding er niet meer te zijn.
 */
let overdracht: FloorToastMessage | null = null;

export function handOverToast(message: FloorToastMessage): void {
  overdracht = message;
}

export function takeHandedOverToast(): FloorToastMessage | null {
  const message = overdracht;
  overdracht = null;
  return message;
}

/** Altijd een dag en een uur, ook vandaag: "ma 14/09 · 09:12" (formulieren-spec §2). */
export function useDayTime() {
  const { t, i18n } = useTranslation();
  return useCallback(
    (iso: string): string => {
      const date = new Date(iso);
      const time = new Intl.DateTimeFormat(i18n.language, {
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
      const weekday = new Intl.DateTimeFormat(i18n.language, { weekday: 'short' })
        .format(date)
        .replace('.', '');
      const dayMonth = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
      return t('floor.dayTime', { day: `${weekday} ${dayMonth}`, time });
    },
    [t, i18n.language],
  );
}
